import { useMemo } from "react";
import { formatDate, formatTime } from "../../../utils/countryLocaleMap";
import { useWidgetLocale } from "./useWidgetLocale";

interface CoreBookingDetails {
  date?: string | null;
  time?: string | null;
  num_people?: number | null;
}

interface UseFormattedBookingInfoParams {
  coreBookingDetails: CoreBookingDetails;
  countryCode: string;
}

interface FormattedBookingInfo {
  formattedDate: string | undefined;
  formattedTime: string | undefined;
  formattedCovers: string | undefined;
}

/**
 * useFormattedBookingInfo — pre-computes formatted date, time, and covers
 * for display in booking info components (sidebar and inline).
 *
 * Extracted from BookingForm to reduce cognitive complexity in JSX.
 */
export function useFormattedBookingInfo({
  coreBookingDetails,
  countryCode,
}: UseFormattedBookingInfoParams): FormattedBookingInfo {
  const timeLocale = useWidgetLocale(countryCode);
  return useMemo(() => {
    const formattedDate = coreBookingDetails.date
      ? formatDate(coreBookingDetails.date, countryCode, "short")
      : undefined;

    const formattedTime =
      coreBookingDetails.time && coreBookingDetails.date
        ? formatTime(coreBookingDetails.time, coreBookingDetails.date, countryCode, timeLocale)
        : undefined;

    const formattedCovers = coreBookingDetails.num_people != null ? String(coreBookingDetails.num_people) : undefined;

    return { formattedDate, formattedTime, formattedCovers };
  }, [coreBookingDetails.date, coreBookingDetails.time, coreBookingDetails.num_people, countryCode, timeLocale]);
}
